import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import GoogleIntegrationDetails from '../shared/models/google/google-integration-details';
import { GoogleIntegrationSetupStatus } from '../shared/models/google/google-integration-setup-status';
import { GoogleIntegrationSummary } from '../shared/models/google/google-integration-summary';
import GoogleIntegrationDiagnostic from '../shared/models/google/google-integration-diagnostic';
import GoogleIntegrationAccountInformation from '../shared/models/google/google-integration-account-information';
import HtmlTagFactory from '../components/specialist/actioncomponents/google-integration-setup/html-tag-factory';


@Injectable({
  providedIn: 'root'
})
export class GoogleIntegrationService {
  private baseUrl = `${environment.urlConfig.serviceUrl}/googleintegration`;

  constructor(private http: HttpClient) { }

  public getDetails(customerId: number): Observable<GoogleIntegrationDetails> {
    return this.http.get<GoogleIntegrationDetails>(`${this.baseUrl}/details/${customerId}`)
      .pipe(map(details => {
        details.GtmNoScriptTag = HtmlTagFactory.generateGtmNoScriptTag(details.GtmId);
        details.GaHtmlTag = HtmlTagFactory.generateGaHtmlTag(details.GaMeasurementId);

        return details;
      }));
  }

  public verifyGtmTag(customerId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/verify/${customerId}`, null);
  }

  public hasCmsAdminCredentials(customerId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/credentials/has-cms-admin/${customerId}`);
  }

  public getIntegrationSetupStatus(customerId: number): Observable<GoogleIntegrationSetupStatus> {
    return this.http.get<GoogleIntegrationSetupStatus>(`${this.baseUrl}/order/status/${customerId}`);
  }

  public getIntegrationSummary(customerId: number): Observable<GoogleIntegrationSummary> {
    return this.http.get<GoogleIntegrationSummary>(`${this.baseUrl}/summary/${customerId}`);
  }

  public getAccountInformation(customerId: number): Observable<GoogleIntegrationAccountInformation> {
    return this.http.get<GoogleIntegrationAccountInformation>(`${this.baseUrl}/account-information/${customerId}`);
  }

  public hasGoogleBusinessProfileAccess(customerId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/google-business-profile/has-access/${customerId}`)
      .pipe(catchError(() => of(false)));
  }

  public orderIntegrationSetup(customerId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/order/${customerId}`, null);
  }

  public orderIntegrationSetupRedo(customerId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/order/redo/${customerId}`, null);
  }

  public closeIntegrationSetupRedo(customerId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/redo/close/${customerId}`, null);
  }

  public getOnboardingUrl(customerId: number): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/onboarding/url/${customerId}`);
  }

  public checkSitePresence(customerId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/site-presence/check/${customerId}`, null);
  }

  public getLatestDiagnostic(customerId: number): Observable<GoogleIntegrationDiagnostic | null> {
    return this.http.get<GoogleIntegrationDiagnostic | null>(`${this.baseUrl}/diagnostic/latest/${customerId}`);
  }

  public correctIntegration(customerId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/correction/${customerId}`, null);
  }

  public correctIntegrationForTask(customerId: number, taskId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/correction/${customerId}/task/${taskId}`, null);
  }

  public isIntegrationCorrectionInProgress(customerId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/correction/in-progress/${customerId}`);
  }
}
